<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import {
  format,
  startOfDay,
  subDays,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  isSameMonth,
  isAfter,
  isToday,
} from 'date-fns';

const props = defineProps({
  currentDateItem: { type: Object, default: null },
});

const emit = defineEmits([
  'navigateToday',
  'navigateYesterday',
  'navigateLastWeek',
  'navigateLastMonth',
  'navigateFirst',
  'navigateDate',
]);

const { t, locale } = useI18n();
const [isOpen, toggleOpen] = useToggle();
const showCalendar = ref(false);
const specificDateBtnRef = ref(null);
const calendarPos = ref({ top: 0, left: 0 });

const today = new Date();
const todayKey = computed(() => format(startOfDay(today), 'yyyy-MM-dd'));
const yesterdayKey = computed(() =>
  format(startOfDay(subDays(today, 1)), 'yyyy-MM-dd')
);

const buttonLabel = computed(() => {
  if (!props.currentDateItem) return t('DATE_NAVIGATOR.TODAY');
  if (props.currentDateItem.dateKey === todayKey.value)
    return t('DATE_NAVIGATOR.TODAY');
  if (props.currentDateItem.dateKey === yesterdayKey.value)
    return t('DATE_NAVIGATOR.YESTERDAY');
  return props.currentDateItem.label;
});

const calendarMonth = ref(startOfMonth(today));

const weekDayLabels = computed(() => {
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
  const bcp47 = locale.value.replace('_', '-');
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return new Intl.DateTimeFormat(bcp47, { weekday: 'narrow' }).format(d);
  });
});

const calendarTitle = computed(() => {
  const bcp47 = locale.value.replace('_', '-');
  return new Intl.DateTimeFormat(bcp47, {
    month: 'long',
    year: 'numeric',
  }).format(calendarMonth.value);
});

const calendarWeeks = computed(() => {
  const monthStart = startOfMonth(calendarMonth.value);
  const monthEnd = endOfMonth(calendarMonth.value);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }
  return weeks;
});

const canGoNext = computed(
  () => !isAfter(addMonths(calendarMonth.value, 1), startOfMonth(today))
);

function prevMonth() {
  calendarMonth.value = subMonths(calendarMonth.value, 1);
}

function nextMonth() {
  if (canGoNext.value) calendarMonth.value = addMonths(calendarMonth.value, 1);
}

function isDayDisabled(day) {
  return isAfter(startOfDay(day), startOfDay(today));
}

function isDayInCurrentMonth(day) {
  return isSameMonth(day, calendarMonth.value);
}

function close() {
  isOpen.value = false;
  showCalendar.value = false;
}

function handleClickOutside() {
  if (isOpen.value) close();
}

function handleCalendarClickOutside() {
  showCalendar.value = false;
}

function selectDay(day) {
  if (isDayDisabled(day)) return;
  close();
  emit('navigateDate', format(day, 'yyyy-MM-dd'));
}

async function openCalendar() {
  showCalendar.value = !showCalendar.value;
  if (!showCalendar.value) return;
  await nextTick();
  if (!specificDateBtnRef.value) return;
  const rect = specificDateBtnRef.value.getBoundingClientRect();
  calendarPos.value = {
    top: rect.top + window.scrollY,
    left: rect.right + 8 + window.scrollX,
  };
}

function goToToday() {
  close();
  emit('navigateToday');
}

function goToYesterday() {
  close();
  emit('navigateYesterday');
}

function goToLastWeek() {
  close();
  emit('navigateLastWeek');
}

function goToLastMonth() {
  close();
  emit('navigateLastMonth');
}

function goToFirst() {
  close();
  emit('navigateFirst');
}
</script>

<template>
  <div class="relative flex justify-center">
    <button
      id="dateNavigatorToggle"
      class="flex items-center gap-1 rounded-full border border-n-weak bg-n-surface-1 px-3 py-1 text-xs text-n-slate-11 hover:bg-n-slate-2 transition-colors shadow-sm"
      @click="toggleOpen(!isOpen)"
    >
      {{ buttonLabel }}
      <span class="i-lucide-chevron-down size-3 shrink-0" />
    </button>

    <div
      v-if="isOpen"
      v-on-click-outside="[
        handleClickOutside,
        { ignore: ['#dateNavigatorToggle'] },
      ]"
      class="absolute top-full mt-1 z-50 min-w-[180px] rounded-lg border border-n-weak bg-n-surface-1 shadow-md py-1"
    >
      <p
        class="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-n-slate-9"
      >
        {{ $t('DATE_NAVIGATOR.GO_TO') }}
      </p>
      <button
        class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 transition-colors"
        @click="goToToday"
      >
        {{ $t('DATE_NAVIGATOR.TODAY') }}
      </button>
      <button
        class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 transition-colors"
        @click="goToYesterday"
      >
        {{ $t('DATE_NAVIGATOR.YESTERDAY') }}
      </button>
      <button
        class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 transition-colors"
        @click="goToLastWeek"
      >
        {{ $t('DATE_NAVIGATOR.LAST_WEEK') }}
      </button>
      <button
        class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 transition-colors"
        @click="goToLastMonth"
      >
        {{ $t('DATE_NAVIGATOR.LAST_MONTH') }}
      </button>
      <button
        class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 transition-colors"
        @click="goToFirst"
      >
        {{ $t('DATE_NAVIGATOR.BEGINNING') }}
      </button>
      <div class="my-1 h-px bg-n-weak" />
      <button
        ref="specificDateBtnRef"
        class="w-full text-left px-3 py-2 text-sm text-n-slate-12 hover:bg-n-slate-2 transition-colors"
        @click="openCalendar"
      >
        {{ $t('DATE_NAVIGATOR.SPECIFIC_DATE') }}
      </button>
    </div>

    <!-- Calendar teleported to body to avoid stacking context issues -->
    <Teleport to="body">
      <div
        v-if="showCalendar"
        v-on-click-outside="handleCalendarClickOutside"
        class="fixed z-[9999] w-56 rounded-lg border border-n-weak bg-n-surface-1 shadow-lg p-2"
        :style="{ top: calendarPos.top + 'px', left: calendarPos.left + 'px' }"
      >
        <div class="flex items-center justify-between mb-1 px-1">
          <button
            class="p-1 rounded hover:bg-n-slate-2 text-n-slate-11 transition-colors"
            @click="prevMonth"
          >
            <span class="i-lucide-chevron-left size-3.5" />
          </button>
          <span class="text-xs font-medium text-n-slate-12 capitalize">{{
            calendarTitle
          }}</span>
          <button
            class="p-1 rounded hover:bg-n-slate-2 text-n-slate-11 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            :disabled="!canGoNext"
            @click="nextMonth"
          >
            <span class="i-lucide-chevron-right size-3.5" />
          </button>
        </div>

        <div class="grid grid-cols-7 mb-0.5">
          <div
            v-for="label in weekDayLabels"
            :key="label"
            class="flex items-center justify-center h-6 text-[10px] font-medium text-n-slate-9"
          >
            {{ label }}
          </div>
        </div>

        <div
          v-for="(week, wi) in calendarWeeks"
          :key="wi"
          class="grid grid-cols-7"
        >
          <button
            v-for="day in week"
            :key="day.getTime()"
            class="flex items-center justify-center w-7 h-7 text-xs rounded-full transition-colors mx-auto"
            :class="{
              'text-n-slate-4 cursor-not-allowed': isDayDisabled(day),
              'text-n-slate-8':
                !isDayInCurrentMonth(day) && !isDayDisabled(day),
              'text-n-slate-12 hover:bg-n-slate-2 cursor-pointer':
                isDayInCurrentMonth(day) && !isDayDisabled(day),
              'ring-1 ring-woot-500 font-semibold':
                isToday(day) && !isDayDisabled(day),
            }"
            :disabled="isDayDisabled(day)"
            @click="selectDay(day)"
          >
            {{ day.getDate() }}
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>
